import { useState, useEffect } from "react";
import { 
  FaPrint, FaEdit, FaUser, FaPhoneAlt, FaProcedures, 
  FaCalendarAlt, FaUserMd, FaMapMarkerAlt 
} from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";
import { HiXMark } from "react-icons/hi2";
import { format } from "date-fns";
import { PrintLayout } from "../PrintLayout";

const HospitalViewAdmissionModal = ({ 
  isOpen, 
  onClose, 
  admission: initialAdmission, 
  onEdit, 
  onDelete 
}) => {
  const [admission, setAdmission] = useState(initialAdmission);

  useEffect(() => {
    setAdmission(initialAdmission);
  }, [initialAdmission]);

  if (!isOpen || !admission) return null;

  const {
    regNo,
    specialNo,
    patientName,
    fatherName,
    motherName,
    relativeName,
    careOf,
    relationship,
    age,
    sex,
    religion,
    address,
    dateOfBirth,
    phoneNumber,
    relativeMobileNumber,
    admissionDate,
    admissionTime,
    consultantName,
    agentName,
    bedNo,
    wardCabin,
    billingType,
    status,
    employeeName
  } = admission;

  // --- Handlers ---
  const handlePrint = () => {
    const elementsToHide = document.querySelectorAll(".print-hidden");
    elementsToHide.forEach((el) => (el.style.display = "none"));

    const printLayout = document.querySelector(".print-layout");
    if (printLayout) {
      printLayout.style.display = "block";
    }
    window.print();
    setTimeout(() => {
      elementsToHide.forEach((el) => (el.style.display = ""));
      if (printLayout) {
        printLayout.style.display = "none";
      }
    }, 500);
  };

  const handleEditClick = () => {
    if (onEdit) {
      onEdit(admission);
      onClose();
    }
  };

  const handleDeleteClick = () => {
    if (onDelete) {
      onDelete(admission.id);
      onClose();
    }
  };

  
  // Header Info for Print
  const admissionHeaderInfo = (
    <>
      <div>
        <p><strong>Reg No:</strong> {regNo || "N/A"}</p>
        <p><strong>Special No:</strong> {specialNo || "N/A"}</p>
        <p><strong>Patient Name:</strong> {patientName || "N/A"}</p>
        <p><strong>Phone:</strong> {phoneNumber || "N/A"}</p>
      </div>
      <div>
        <p><strong>Admission Date:</strong> {admissionDate ? format(new Date(admissionDate), "dd MMM yyyy") : "N/A"}</p>
        <p><strong>Time:</strong> {admissionTime || "N/A"}</p>
        <p><strong>Bed No:</strong> {bedNo || "N/A"}</p>
        <p><strong>Status:</strong> {status}</p>
      </div>
    </>
  );

  const detailTableContent = (
    <div className="w-full mt-4">
      <h3 className="border-b border-gray-300 font-bold mb-4 uppercase text-sm">Patient Information</h3>
      <table className="w-full text-xs border-collapse">
        <tbody>
          <tr>
            <td className="p-1 font-bold w-1/4">Father's / Husband's Name:</td>
            <td className="p-1 w-1/4">{fatherName || "N/A"}</td>
            <td className="p-1 font-bold w-1/4">Mother's Name:</td>
            <td className="p-1 w-1/4">{motherName || "N/A"}</td>
          </tr>
          <tr>
            <td className="p-1 font-bold">Age / Sex:</td>
            <td className="p-1">{age || "N/A"} / {sex || "N/A"}</td>
            <td className="p-1 font-bold">Religion:</td>
            <td className="p-1">{religion || "N/A"}</td>
          </tr>
          <tr>
            <td className="p-1 font-bold">Address:</td>
            <td className="p-1" colSpan="3">{address || "N/A"}</td>
          </tr>
          <tr>
            <td className="p-1 font-bold">Guardian / Relative:</td>
            <td className="p-1">{relativeName || "N/A"} ({relationship})</td>
            <td className="p-1 font-bold">Relative Mobile:</td>
            <td className="p-1">{relativeMobileNumber || "N/A"}</td>
          </tr>
        </tbody>
      </table>

      <h3 className="border-b border-gray-300 font-bold mb-4 mt-4 uppercase text-sm">Admission Details</h3>
      <table className="w-full text-xs border-collapse">
        <tbody>
          <tr>
            <td className="p-1 font-bold w-1/4">Consultant:</td>
            <td className="p-1 w-1/4">{consultantName || "N/A"}</td>
            <td className="p-1 font-bold w-1/4">Agent:</td>
            <td className="p-1 w-1/4">{agentName || "N/A"}</td>
          </tr>
          <tr>
            <td className="p-1 font-bold">Ward/Cabin:</td>
            <td className="p-1">{wardCabin || "N/A"}</td>
            <td className="p-1 font-bold">Billing Type:</td>
            <td className="p-1">{billingType || "Normal"}</td>
          </tr>
          <tr>
            <td className="p-1 font-bold">Admitted By:</td>
            <td className="p-1" colSpan="3">{employeeName || "N/A"}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );

  const DetailBlock = ({ label, value, icon }) => (
    <div className="flex flex-col mb-1">
      <span className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-0.5 flex items-center gap-1">
        {icon && <span className="text-gray-400">{icon}</span>}
        {label}
      </span>
      <span className="text-sm font-medium text-gray-800 break-words">{value || "—"}</span>
    </div>
  );

return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4 print-hidden transition-opacity duration-300">
        <div className="bg-white shadow-2xl w-full max-w-5xl rounded-xl overflow-hidden flex flex-col max-h-[90vh] animate-fadeIn">
          
          {/* --- Modal Header --- */}
          <div className="flex items-center justify-between gap-3 flex-wrap bg-gray-200 border-b border-dashed border-gray-300 p-4 print-hidden">
            <div>
              <h2 className="text-xl font-bold text-gray-800">Admission Details</h2>
              <p className="text-xs text-gray-500 mt-0.5">View full patient admission information</p>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={handlePrint}
                className="size-7 rounded-full bg-white text-M-primary-color text-sm inline-flex items-center justify-center hover:bg-M-primary-color hover:text-white transition-all duration-300"
                title="Print"
              >
                <FaPrint />
              </button>
              <button
                onClick={handleEditClick}
                className="size-7 rounded-full bg-white text-M-Green-color text-sm inline-flex items-center justify-center hover:bg-M-Green-color hover:text-white transition-all duration-300"
                title="Edit"
              >
                <FaEdit />
              </button>
              <button
                onClick={handleDeleteClick}
                className="size-7 rounded-full bg-white text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
                title="Delete"
              >
                <RiDeleteBin6Line />
              </button>
              <button
                onClick={onClose}
                className="size-7 rounded-full bg-white text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
                title="Close"
              >
                <HiXMark />
              </button>
            </div>
          </div>

          {/* --- Modal Body --- */}
          <div className="p-6 overflow-y-auto bg-gray-50/50">
            
            {/* 1. Hero Section (Patient Card) */}
            <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-100 mb-6 flex flex-col md:flex-row justify-between md:items-center gap-4">
              <div className="flex items-start gap-4">
                <div className="size-14 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 text-2xl">
                  <FaUser />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-800">{patientName || "Unknown Patient"}</h3>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600 mt-1">
                    <span className="flex items-center gap-1"><span className="font-semibold">Reg:</span> {regNo}</span>
                    <span className="w-1 h-1 rounded-full bg-gray-300 self-center"></span>
                    <span>{age} Yrs</span>
                    <span className="w-1 h-1 rounded-full bg-gray-300 self-center"></span>
                    <span>{sex}</span>
                    {specialNo && (
                      <>
                        <span className="w-1 h-1 rounded-full bg-gray-300 self-center"></span>
                        <span className="text-blue-600 font-medium">Special: {specialNo}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="px-4 py-2 bg-blue-50 rounded-lg text-center min-w-[100px]">
                  <p className="text-xs text-blue-500 font-bold uppercase">Bed No</p>
                  <p className="text-lg font-bold text-blue-700">{bedNo || "N/A"}</p>
                </div>
                <div className={`px-4 py-2 rounded-lg text-center min-w-[100px] ${status === 'Released' ? 'bg-green-50' : 'bg-orange-50'}`}>
                  <p className={`text-xs font-bold uppercase ${status === 'Released' ? 'text-green-500' : 'text-orange-500'}`}>Status</p>
                  <p className={`text-lg font-bold ${status === 'Released' ? 'text-green-700' : 'text-orange-700'}`}>{status || "Admitted"}</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* 2. Admission Info Column */}
              <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-100">
                <h4 className="text-sm font-bold text-gray-800 uppercase border-b pb-2 mb-4 flex items-center gap-2">
                  <FaProcedures className="text-blue-500" /> Admission Details
                </h4>
                <div className="space-y-4">
                  <DetailBlock label="Admission Date" value={admissionDate ? format(new Date(admissionDate), "dd MMM yyyy") : ""} icon={<FaCalendarAlt size={10}/>} />
                  <DetailBlock label="Admission Time" value={admissionTime} />
                  <DetailBlock label="Consultant" value={consultantName} icon={<FaUserMd size={10}/>} />
                  <DetailBlock label="Ward / Cabin" value={wardCabin} />
                  <DetailBlock label="Billing Type" value={billingType} />
                  <DetailBlock label="Admitted By" value={employeeName} />
                </div>
              </div>

              {/* 3. Personal Info Column */}
              <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-100">
                <h4 className="text-sm font-bold text-gray-800 uppercase border-b pb-2 mb-4 flex items-center gap-2">
                  <FaUser className="text-green-500" /> Personal Info
                </h4>
                <div className="space-y-4">
                  <DetailBlock label="Father's / Husband's Name" value={fatherName} />
                  <DetailBlock label="Mother's Name" value={motherName} />
                  <DetailBlock label="Date of Birth" value={dateOfBirth ? format(new Date(dateOfBirth), "dd MMM yyyy") : ""} />
                  <DetailBlock label="Religion" value={religion} />
                  <DetailBlock label="Phone Mobile" value={phoneNumber} icon={<FaPhoneAlt size={10}/>} />
                  <DetailBlock label="Address" value={address} icon={<FaMapMarkerAlt size={10}/>} />
                </div>
              </div>

              {/* 4. Contact/Other Column */}
              <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-100">
                <h4 className="text-sm font-bold text-gray-800 uppercase border-b pb-2 mb-4 flex items-center gap-2">
                  <FaPhoneAlt className="text-orange-500" /> Emergency / Other
                </h4>
                <div className="space-y-4">
                  <DetailBlock label="Relative Name" value={relativeName} />
                  <DetailBlock label="Relationship" value={relationship} />
                  <DetailBlock label="Relative Mobile" value={relativeMobileNumber} />
                  <DetailBlock label="Care Of (C/O)" value={careOf} />
                  <div className="pt-2 border-t border-dashed mt-2">
                    <DetailBlock label="Ref / Agent" value={agentName} />
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Hidden Print Layout */}
      <PrintLayout
        className="print-layout"
        title="Admission Ticket"
        billInfo={admissionHeaderInfo}
        table={detailTableContent}
      />
    </>
  );
};


export default HospitalViewAdmissionModal;